import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuthStore } from '../../stores/root_store';
import { jobApplicationApi, type SampleTask } from '../../api/job_application_api';

export const APPLICATION_TABS = [
  { slug: 'review', label: 'Needs review' },
  { slug: 'accepted', label: 'Accepted' },
  { slug: 'failed', label: 'Failed' },
  { slug: 'all', label: 'All' },
];

function matchesTab(item: SampleTask, tab: string): boolean {
  if (tab === 'review') return !item.outcome;
  if (tab === 'accepted') return item.outcome === 'accept';
  if (tab === 'failed') return item.outcome === 'failed' || item.outcome === 'fail';
  return true;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function OutcomeChip({ outcome }: { outcome: string | null }) {
  if (outcome === 'accept') return <Chip size="small" color="success" label="Accepted" />;
  if (outcome === 'failed' || outcome === 'fail') return <Chip size="small" color="error" label="Failed" />;
  return <Chip size="small" color="warning" variant="outlined" label="Needs review" />;
}

export function LikelyChip({ likely }: { likely: SampleTask['likely_outcome'] }) {
  return likely === 'failed'
    ? <Chip size="small" variant="outlined" label="Quiz: missed some" />
    : <Chip size="small" variant="outlined" color="success" label="Quiz: all correct" />;
}

export const ManagerApplicationsPage = observer(() => {
  const authStore = useAuthStore();
  const navigate = useNavigate();
  const { tab = 'review' } = useParams<{ tab?: string }>();

  const [items, setItems] = useState<SampleTask[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canView = authStore.roles.includes('manager') || !!authStore.user?.is_manager;

  const load = useCallback(async (from?: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await jobApplicationApi.list(from);
      setItems((prev) => (from ? [...prev, ...result.items] : result.items));
      setCursor(result.cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  if (authStore.initialized && !canView) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Container>
    );
  }

  const visible = items.filter((x) => matchesTab(x, tab));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/manager')} sx={{ mb: 2 }}>
        Manager Tools
      </Button>
      <Typography variant="h4" gutterBottom>Assistant Applications</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Review sample task replies and accept or fail each applicant. Accepting makes them an assistant.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => navigate(`/manager/applications/${v}`)} sx={{ mb: 2 }}>
        {APPLICATION_TABS.map((t) => (
          <Tab key={t.slug} value={t.slug} label={t.label} />
        ))}
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <CardContent>
          {loading && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : visible.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No applications here{cursor ? ' yet; load more to look further back.' : '.'}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Applicant</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Applied</TableCell>
                    <TableCell>Quiz</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((item) => (
                    <TableRow
                      key={item.uuid}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/manager/application/${item.uuid}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.applicant?.name ?? 'Unknown applicant'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.applicant?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {[item.applicant?.home_city, item.applicant?.home_state].filter(Boolean).join(', ') || '—'}
                      </TableCell>
                      <TableCell>{formatDate(item.date_created)}</TableCell>
                      <TableCell><LikelyChip likely={item.likely_outcome} /></TableCell>
                      <TableCell><OutcomeChip outcome={item.outcome} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {cursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button onClick={() => load(cursor)} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Load more'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
});
